import debounce from 'lodash/debounce';
import omit from 'lodash/omit';
import isNil from 'lodash/isNil';
import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import SHGlobals from 'sh-globals';
import DOMUtils from '@spothero/utils/dom';
import SegmentUtils from 'utils/segment';
import SearchUtils from 'search-utils';
import DidYouMeanDialog from 'search/components/common/DidYouMeanDialog';
import {setNewData} from '../common/search-actions';
import Navigation from './navigation/Navigation';
import WidgetContent from './WidgetContent';
import GDPRDialog from 'global/components/common/utils/GDPRDialog';

class Widget extends Component {
    constructor(props) {
        super(props);

        const {
            hourSpan
        } = props;
        let showGDPRDialog = false;
        let userCountryCode = null;

        this._onWindowResize = this._onWindowResize.bind(this);
        this._onLogoClick = this._onLogoClick.bind(this);
        this._onMultiOrNoResultsFound = this._onMultiOrNoResultsFound.bind(this);
        this._onNoAddressFoundDialogHidden = this._onNoAddressFoundDialogHidden.bind(this);

        if (SHGlobals.cloudfrontViewerCountryCode
            && SHGlobals.cloudfrontViewerCountryCode !== 'US'
            && SHGlobals.cloudfrontViewerCountryCode !== 'CA'
        ) {
            showGDPRDialog = true;
            userCountryCode = SHGlobals.cloudfrontViewerCountryCode;
        }

        this.state = {
            searchAddressDialogResults: null,
            searchAddressDialogExtraData: null,
            hourSpan,
            showGDPRDialog,
            userCountryCode
        };
    }

    componentDidMount() {
        window.addEventListener('resize', debounce(this._onWindowResize, 100));

        this._onWindowResize();

        SegmentUtils.track({
            event: 'Search Landing'
        });
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this._onWindowResize);
    }

    _onGDPRDialogHidden = () => {
        this.setState({
            showGDPRDialog: false
        });
    }

    _onMultiOrNoResultsFound(results, extraSearchData) {
        this.setState({
            searchAddressDialogResults: results,
            searchAddressDialogExtraData: extraSearchData
        });
    }

    _onNoAddressFoundDialogHidden(result) {
        // if multiple results are shown in the dialog then clicking a result should fire the selection process
        if (!isNil(result)) {
            const {
                setNewSearchData
            } = this.props;
            const {
                searchAddressDialogExtraData
            } = this.state;

            if (searchAddressDialogExtraData) {
                setNewSearchData({
                    params: {
                        ...searchAddressDialogExtraData,
                        ...result
                    }
                });
            } else {
                setNewSearchData({
                    data: {
                        currentPage: 'map'
                    },
                    params: omit(SearchUtils.getAddressData(result), ['city', 'state', 'zipCode'])
                });
            }
        }

        this.setState({
            searchAddressDialogResults: null,
            searchAddressDialogExtraData: null
        });
    }

    _onWindowResize() {
        if (this.props.container.offsetWidth < 580) {
            DOMUtils.addClass('.spothero-html', ['SH-Widget-small', 'mobile']);
        } else {
            DOMUtils.removeClass('.spothero-html', ['SH-Widget-small', 'mobile']);
        }
    }

    _onLogoClick(evt) {
        const {
            searchType,
            currentPage,
            zoom,
            selectedEvent,
            venueUrl,
            siteUrl,
            searchParams: {
                starts,
                ends,
                latitude,
                longitude,
                monthly,
                operator_id: operatorId
            }
        } = this.props;
        let url = siteUrl;

        if (currentPage === 'events') {
            url = `${siteUrl}/${venueUrl}`;
        } else if (currentPage === 'map') {
            if (searchType === 'event') {
                url = `${siteUrl}/${venueUrl}/${selectedEvent.seo_url}`;
            } else {
                const operatorIdUrlParam = isNil(operatorId) ? '' : `&operator_id=${operatorId}`;

                url = `${siteUrl}/search/?starts=${starts}&latitude=${latitude}&longitude=${longitude}&zoom=${zoom}&monthly=${monthly}${operatorIdUrlParam}`;

                if (!monthly) {
                    url += `&ends=${ends}`;
                }
            }
        }

        window.open(url);
    }

    render() {
        const {
            container,
            siteUrl,
            searchType,
            currentPage,
            backgroundColor,
            kmlMapLayerURL,
            title,
            subtitle,
            showDefaultSubtitle,
            unbranded,
            venueUrl,
            eventTypes,
            affiliate,
            destinationTitle,
            zoom,
            searchParams: {
                monthly
            }
        } = this.props;
        const {
            searchAddressDialogResults,
            hourSpan,
            showGDPRDialog,
            userCountryCode
        } = this.state;
        const showNav = (currentPage !== 'search' && !unbranded);

        return (
            <div className="SH-Widget">
                {showNav &&
                    <Navigation
                        title={title}
                        subtitle={subtitle}
                        showDefaultSubtitle={showDefaultSubtitle}
                        unbranded={unbranded}
                        onLogoClick={this._onLogoClick}
                        container={container}
                    />
                }
                <WidgetContent
                    container={container}
                    backgroundColor={backgroundColor}
                    kmlMapLayerURL={kmlMapLayerURL}
                    unbranded={unbranded}
                    title={title}
                    subtitle={subtitle}
                    siteUrl={siteUrl}
                    searchType={searchType}
                    currentPage={currentPage}
                    venueUrl={venueUrl}
                    eventTypes={eventTypes}
                    destinationTitle={unescape(destinationTitle)}
                    affiliate={affiliate}
                    zoom={zoom}
                    monthly={monthly}
                    hourSpan={hourSpan}
                    onLogoClick={this._onLogoClick}
                    onMultiOrNoResultsFound={this._onMultiOrNoResultsFound}
                />
                {searchAddressDialogResults &&
                    <DidYouMeanDialog
                        className="SH-DidYouMeanDialog"
                        results={searchAddressDialogResults}
                        onHidden={this._onNoAddressFoundDialogHidden}
                    />
                }
                {showGDPRDialog &&
                    <GDPRDialog
                        onHidden={this._onGDPRDialogHidden}
                        countryCode={userCountryCode}
                    />
                }
            </div>
        );
    }
}

Widget.propTypes = {
    container: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.object
    ]).isRequired,
    searchParams: PropTypes.object.isRequired,
    searchType: PropTypes.oneOf(['address', 'event']),
    currentPage: PropTypes.string,
    title: PropTypes.string,
    subtitle: PropTypes.string,
    showDefaultSubtitle: PropTypes.bool,
    destinationTitle: PropTypes.string,
    hourSpan: PropTypes.number.isRequired,
    zoom: PropTypes.number.isRequired,
    unbranded: PropTypes.bool,
    venueUrl: PropTypes.string,
    affiliate: PropTypes.number,
    eventTypes: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number
    ]),
    siteUrl: PropTypes.string,
    backgroundColor: PropTypes.string,
    kmlMapLayerURL: PropTypes.string,
    selectedEvent: PropTypes.object,
    setNewSearchData: PropTypes.func.isRequired
};

Widget.defaultProps = {
    searchType: 'address',
    destinationTitle: 'Destination',
    hourSpan: 3,
    siteUrl: 'https://spothero.com',
    backgroundColor: '002d5b'
};

const mapStateToProps = state => {
    const {
        search: {
            data: {
                searchType,
                currentPage,
                zoom,
                destinationTitle,
                selectedEvent,
                params: searchParams
            }
        }
    } = state;

    return {
        searchType,
        currentPage,
        zoom,
        destinationTitle,
        searchParams,
        selectedEvent
    };
};

const mapDispatchToProps = {
    setNewSearchData: setNewData
};

export default connect(mapStateToProps, mapDispatchToProps)(Widget);



// WEBPACK FOOTER //
// ./widget/src/js/components/Widget.jsx